import React from 'react';
import { Box } from '@material-ui/core';
import { SampleProducts } from './IllustrativeContent';
import getPreviewDomProps from '../getPreviewDomProps';

export const getProductGridStyles = ({ columns, gap, horizontal }) => {
  return {
    gap: horizontal ? Math.min(gap, columns >= 4 ? 4 : 8) : gap,
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
  };
};

/**
 * Products Block Component
 * Schematic representation of product grid
 */
const Products = ({
  illustrative = false,
  maxItems = 12,
  productsInRow = 4,
  gap = 16,
  cardType = 'standard',
  theme,
  showImage = true,
  showPrice = true,
  showTitle = true,
  showRating,
  showAddToCart,
  showWishlist,
  ...props
}) => {
  if (illustrative) return <SampleProducts {...{ maxItems, productsInRow, gap, cardType, theme, showImage, showPrice, showTitle, showRating, showAddToCart, showWishlist }} />;

  // if (hidden) return null; // Removed as per request (handled by container)

  const cols = parseInt(productsInRow, 10) || 4;
  const itemCount = parseInt(maxItems, 10) || 12;

  const isSimple = cardType === 'simple';
  const isHorizontal = cardType === 'horizontal';
  const isDenseHorizontal = isHorizontal && cols >= 4;
  const gridStyles = getProductGridStyles({ columns: cols, gap, horizontal: isHorizontal });

  const primaryColor = theme?.palette?.primary?.main || '#e0e0e0';
  const domProps = getPreviewDomProps(props);

  return (
    <Box
      display="grid"
      gridGap={`${gridStyles.gap}px`}
      style={{
        gridTemplateColumns: gridStyles.gridTemplateColumns,
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
      {...domProps}
    >
      {Array.from({ length: itemCount }).map((_, i) => (
        <Box
          // eslint-disable-next-line react/no-array-index-key
          key={`prod-${i}`}
          display="flex"
          flexDirection={isHorizontal ? 'row' : 'column'}
          width="100%"
          height={isHorizontal ? 48 : 'auto'}
          minWidth={0}
          overflow="hidden"
          alignItems={isHorizontal ? 'center' : 'stretch'}
          style={{
            boxSizing: 'border-box',
            padding: isHorizontal ? (isDenseHorizontal ? 2 : 4) : 0,
            border: isHorizontal ? '1px solid #e2ebe7' : 0,
            borderRadius: isHorizontal ? 5 : 0,
            backgroundColor: isHorizontal ? 'rgba(255, 255, 255, 0.94)' : 'transparent',
          }}
        >
          {/* Image Placeholder */}
          {showImage && (
            <Box
              width={isHorizontal ? (isDenseHorizontal ? 20 : 40) : '100%'}
              height={isHorizontal ? (isDenseHorizontal ? 40 : 38) : 'auto'}
              paddingBottom={isHorizontal ? 0 : '130%'} // Aspect ratio ~3:4 for vertical
              bgcolor="#f0f2f5"
              borderRadius={4}
              marginBottom={isHorizontal ? 0 : 1}
              marginRight={isHorizontal ? (isDenseHorizontal ? 0.5 : 1) : 0}
              position="relative"
              style={{
                backgroundColor: `${primaryColor}15`,
                flex: isHorizontal ? `0 1 ${isDenseHorizontal ? 20 : 40}px` : '0 0 auto',
                maxWidth: isHorizontal ? '40%' : '100%',
                minWidth: 0,
              }}
            />
          )}

          {/* Content Placeholders */}
          {!isSimple && (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              width="100%"
              minWidth={0}
              overflow="hidden"
            >
              {showTitle && (
                <Box
                  width="85%"
                  height={isDenseHorizontal ? 6 : 8}
                  bgcolor="#f0f2f5"
                  borderRadius={2}
                  marginBottom={0.5}
                />
              )}
              <Box
                width="60%"
                height={isDenseHorizontal ? 5 : 7}
                bgcolor="#f0f2f5"
                borderRadius={2}
                marginBottom={isDenseHorizontal ? 0.5 : 1}
              />
              {showPrice && (
                <Box width="40%" height={isDenseHorizontal ? 7 : 9} bgcolor={primaryColor} borderRadius={2} />
              )}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default Products;
